import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Check } from "lucide-react";
import churchWorkshop from "@/assets/church-workshop.jpg";

const audiences = [
  "Pastors and ministry leaders",
  "Church boards and elders",
  "Ministry staff and volunteers",
  "Small group leaders",
  "Entire congregations",
];

export function ChurchTraining() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={churchWorkshop}
          alt="Diverse group of church leaders in a training workshop"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground/95 via-foreground/85 to-foreground/70" />
      </div>

      <div className="container relative z-10 px-6">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-14"
          >
            <p className="text-sm md:text-base font-semibold uppercase tracking-[0.2em] text-accent mb-6">
              Church Training
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-7xl font-bold text-primary-foreground mb-8 leading-[1.1]">
              Building Churches
              <br />
              Where{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Wolves</span>
                <motion.svg
                  className="absolute -bottom-1 left-0 w-full z-0"
                  viewBox="0 0 200 8"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.3 }}
                >
                  <motion.path
                    d="M2 5C40 2 80 6 100 4C120 2 160 6 198 3"
                    stroke="hsl(0, 84%, 60%)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    whileInView={{ pathLength: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.3 }}
                  />
                </motion.svg>
              </span>
              <br />
              <span className="text-accent">Cannot Hide</span>
            </h2>
            <p className="text-xl md:text-2xl text-primary-foreground/70 leading-relaxed max-w-2xl">
              Healthy churches build cultures of awareness, accountability, and
              spiritual safety. Soul Safe Sanctuary™ equips pastors, leaders, and
              congregations with training to recognize manipulation and prevent
              spiritual abuse, and certification formally registers your ministry
              as a Safe Church committed to these standards.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-14"
          >
            <h3 className="text-sm md:text-base font-semibold uppercase tracking-[0.2em] text-primary-foreground/50 mb-8">
              Train Your Church Community
            </h3>
            <ul className="space-y-5">
              {audiences.map((item, i) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: -15 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: 0.3 + i * 0.08 }}
                  className="flex items-center gap-4 text-xl md:text-2xl text-primary-foreground"
                >
                  <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-accent" />
                  </div>
                  {item}
                </motion.li>
              ))}
            </ul>

            <p className="mt-12 text-2xl md:text-3xl font-serif italic text-primary-foreground/50">
              <span className="relative inline-block">
                <span className="relative z-10">Education</span>
                {/* Red circle - pure CSS animation for persistence */}
                <svg
                  className="absolute -inset-2 md:-inset-3 z-0"
                  viewBox="0 0 160 80"
                  fill="none"
                  style={{ overflow: 'visible' }}
                >
                  <ellipse
                    cx="80"
                    cy="40"
                    rx="75"
                    ry="35"
                    stroke="hsl(0, 84%, 60%)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    fill="none"
                    className="animate-draw-circle"
                  />
                </svg>
              </span>{" "}
              is one of the most powerful forms of{" "}
              <span className="relative inline-block">
                <span className="relative z-10">reform</span>
                <svg
                  className="absolute -bottom-1 left-0 w-full z-0"
                  viewBox="0 0 200 8"
                  fill="none"
                  style={{ overflow: 'visible' }}
                >
                  <path
                    d="M2 5C40 2 80 6 100 4C120 2 160 6 198 3"
                    stroke="hsl(0, 84%, 60%)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    className="animate-draw-line"
                  />
                </svg>
              </span>.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.4 }}
          >
            <Button
              onClick={() => {
                if (loading) return;
                navigate(user ? "/pricing" : "/auth");
              }}
              className="h-16 px-10 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-full shadow-[0_8px_30px_-6px_hsl(174,57%,39%,0.4)]"
            >
              Train Your Church
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
